import { t } from "@lingui/macro";
import {
  AvailableWorkTypesForBulkMovingTimeTrackingItemsDocument,
  type AvailableWorkTypesForBulkMovingTimeTrackingItemsQuery,
  type AvailableWorkTypesForBulkMovingTimeTrackingItemsQueryVariables,
  CheckTimeTrackingItemsLockedDocument,
  type CheckTimeTrackingItemsLockedQuery,
  type CheckTimeTrackingItemsLockedQueryVariables,
  GetOurWorkBudgetItemsSimpleMapOptionsDocument,
  type GetOurWorkBudgetItemsSimpleMapOptionsQuery,
  type GetOurWorkBudgetItemsSimpleMapOptionsQueryVariables,
  GetProjectsWithPositionsDocument,
  type GetProjectsWithPositionsQuery,
  type GetProjectsWithPositionsQueryVariables,
  GetTasksSimpleMapOptionsDocument,
  type GetTasksSimpleMapOptionsQuery,
  type GetTasksSimpleMapOptionsQueryVariables,
  MoveTimeTrackingItemsToTaskDocument,
  type MoveTimeTrackingItemsToTaskMutation,
  type MoveTimeTrackingItemsToTaskMutationVariables,
  type TimeTrackingWorkType,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { isTrackingEnabled } from "@src/utils/time-tracking";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

type ProjectOption = IOption & {
  positions: Pick<TimeTrackingWorkType, "id" | "title">[];
};

export type MatchedPositionsState = "all" | "some" | "none";

export class MoveHoursToProjectStore {
  @observable.ref selectedIds: string[] = [];
  @observable.ref distinctWorkTypes: { id: string; title: string }[] = [];
  @observable taskOptions: IOption[] = [];
  @observable budgetItemOptions: IOption[] = [];
  @observable projectOptions: ProjectOption[] = [];
  brandOptions: IOption[] = [];
  @observable missingPositions: IOption[] = [];
  @observable matchedPositions: IOption[] = [];
  @observable isProjectOptionsLoading = false;
  @observable isBudgetItemOptionsLoading = false;
  @observable isTaskOptionsLoading = false;
  @observable isWorkTypesLoading = false;

  private projectsFetcher = new FetchHelper<
    GetProjectsWithPositionsQuery,
    GetProjectsWithPositionsQueryVariables
  >(GetProjectsWithPositionsDocument);

  private budgetItemFetcher = new FetchHelper<
    GetOurWorkBudgetItemsSimpleMapOptionsQuery,
    GetOurWorkBudgetItemsSimpleMapOptionsQueryVariables
  >(GetOurWorkBudgetItemsSimpleMapOptionsDocument);

  private taskFetcher = new FetchHelper<
    GetTasksSimpleMapOptionsQuery,
    GetTasksSimpleMapOptionsQueryVariables
  >(GetTasksSimpleMapOptionsDocument);

  private moveMutator = new MutationHelper<
    MoveTimeTrackingItemsToTaskMutation,
    MoveTimeTrackingItemsToTaskMutationVariables
  >(MoveTimeTrackingItemsToTaskDocument);

  private checkLockedFetcher = new FetchHelper<
    CheckTimeTrackingItemsLockedQuery,
    CheckTimeTrackingItemsLockedQueryVariables
  >(CheckTimeTrackingItemsLockedDocument);

  private workTypesFetcher = new FetchHelper<
    AvailableWorkTypesForBulkMovingTimeTrackingItemsQuery,
    AvailableWorkTypesForBulkMovingTimeTrackingItemsQueryVariables
  >(AvailableWorkTypesForBulkMovingTimeTrackingItemsDocument);

  @observable time_tracking_work_type_mapping = new FormState<
    FormState<{
      from_time_tracking_work_type_id: FieldState<TimeTrackingWorkType["id"]>;
      to_time_tracking_work_type_id: FieldState<
        TimeTrackingWorkType["id"] | undefined
      >;
    }>[]
  >([]);
  brand_id = new FieldState<string | undefined>(undefined).validators(required);
  project_id = new FieldState<string | undefined>(undefined).validators(
    required,
  );
  budget_item_id = new FieldState<string | undefined>(undefined).validators(
    required,
  );
  task_id = new FieldState<string | undefined>(undefined).validators(required);

  form = new FormState([
    this.brand_id,
    this.project_id,
    this.budget_item_id,
    this.task_id,
    this.time_tracking_work_type_mapping,
  ]);

  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  @computed
  get isMoveLoading(): boolean {
    return (
      this.checkLockedFetcher.isLoading.value ||
      this.moveMutator.isLoading.value
    );
  }

  @computed get selectedProjectOption(): ProjectOption | undefined {
    return this.projectOptions.find(
      ({ value }) => value === this.project_id.value,
    );
  }

  @computed get selectedProjectPositionOptions(): IOption[] {
    return (
      this.selectedProjectOption?.positions.map(({ title, id }) => ({
        label: title,
        value: id,
      })) ?? []
    );
  }

  @computed get matchedPositionsState(): MatchedPositionsState | undefined {
    if (!this.project_id.value) {
      return undefined;
    } else if (this.matchedPositions.length === 0) {
      return "none";
    } else if (
      this.distinctWorkTypes.length > 0 &&
      this.matchedPositions.length === this.distinctWorkTypes.length
    ) {
      return "all";
    } else {
      return "some";
    }
  }

  @action.bound
  async getProjectOptions() {
    if (!this.brand_id.value) return;

    this.projectOptions = [];
    this.isProjectOptionsLoading = true;
    const [data] = await this.projectsFetcher.fetch({
      brandId: this.brand_id.value,
    });
    this.isProjectOptionsLoading = false;

    const projects = data?.brand?.projects ?? [];

    this.projectOptions = mapToOptions
      .projects(projects)
      .map((option, index) => ({
        positions: projects[index].availableTimeTrackingWorkTypes,
        ...option,
      }));
  }

  @action.bound
  async getBudgetItemOptions() {
    if (!this.project_id.value) return;

    this.budgetItemOptions = [];
    this.isBudgetItemOptionsLoading = true;
    const [data] = await this.budgetItemFetcher.fetch({
      project_id: this.project_id.value,
    });
    this.isBudgetItemOptionsLoading = false;

    this.budgetItemOptions = mapToOptions.toOptions(
      data?.ourWorkBudgetItemsSimpleMap.filter((budgetItem) =>
        isTrackingEnabled(budgetItem.timeTrackingSettings),
      ) ?? [],
      "title",
    );

    if (this.budgetItemOptions.length === 1) {
      this.budget_item_id.onChange(this.budgetItemOptions[0].value);
      await this.getTaskOptions();
    }
  }

  @action.bound
  async getTaskOptions() {
    if (!this.budget_item_id.value) return;

    this.taskOptions = [];
    this.isTaskOptionsLoading = true;
    const [data] = await this.taskFetcher.fetch({
      our_work_budget_item_id: this.budget_item_id.value,
    });
    this.isTaskOptionsLoading = false;

    this.taskOptions = mapToOptions.toOptions(
      data?.tasksSimpleMap ?? [],
      "name",
    );
    if (this.taskOptions.length === 1) {
      this.task_id.onChange(this.taskOptions[0].value);
    }
  }

  @action.bound
  setSelectedIds(ids: string[]) {
    this.selectedIds = ids;
  }

  @action.bound
  async fetchDistinctWorkTypes() {
    if (this.selectedIds.length === 0) {
      this.distinctWorkTypes = [];
      return;
    }

    this.isWorkTypesLoading = true;
    const [data, error] = await this.workTypesFetcher.fetch({
      itemIds: this.selectedIds,
    });
    this.isWorkTypesLoading = false;

    if (error || !data?.getAvailableWorkTypesForBulkMovingTimeTrackingItems) {
      this.distinctWorkTypes = [];
      return;
    }

    this.distinctWorkTypes =
      data.getAvailableWorkTypesForBulkMovingTimeTrackingItems;

    if (this.project_id.value) {
      this.resolvePositions();
    }
  }

  @action.bound
  resolvePositions() {
    this.time_tracking_work_type_mapping.$ = [];
    this.missingPositions = [];
    this.matchedPositions = [];

    this.distinctWorkTypes.forEach((workType) => {
      const foundPosition = this.selectedProjectOption?.positions.find(
        ({ id }) => id === workType.id,
      );
      if (foundPosition) {
        this.matchedPositions.push({
          label: foundPosition.title,
          value: foundPosition.id,
        });
      } else {
        if (this.missingPositions.find((p) => p.value === workType.id)) return;

        this.missingPositions.push({
          label: workType.title,
          value: workType.id,
        });
        this.time_tracking_work_type_mapping.$.push(
          new FormState({
            from_time_tracking_work_type_id: new FieldState(workType.id),
            to_time_tracking_work_type_id: new FieldState<string | undefined>(
              undefined,
            ).validators(required),
          }),
        );
      }
    });
  }

  @action.bound
  async getUnlockedItemIds(ids: string[]): Promise<string[]> {
    if (ids.length === 0) return [];

    const [data] = await this.checkLockedFetcher.fetch({ itemIds: ids });

    const lockedIds = new Set(data?.checkTimeTrackingItemsLocked ?? []);

    if (lockedIds.size === 0) return ids;

    const unlockedIds = ids.filter((id) => !lockedIds.has(id));

    this.appStore.UIStore.toast({
      status: "info",
      title: t`${ids.length - unlockedIds.length} items are locked and changes won't be applied.`,
    });

    return unlockedIds;
  }

  @action.bound
  async moveItems(): Promise<boolean> {
    const { hasError } = await this.form.validate();
    if (hasError) return false;

    const unlockedIds = await this.getUnlockedItemIds(this.selectedIds);

    if (unlockedIds.length === 0) return false;

    const variables = this.serialize();
    variables.item_ids = unlockedIds;

    const [data, error] = await this.moveMutator.mutate(variables);
    if (error || !data?.moveTimeTrackingItemsToTask) return false;

    this.appStore.UIStore.toast({
      title: t`${data.moveTimeTrackingItemsToTask.successful} items have been moved`,
      status: "success",
    });
    return true;
  }

  serialize(): MoveTimeTrackingItemsToTaskMutationVariables {
    return {
      task_id: this.task_id.value!,
      item_ids: this.selectedIds,
      time_tracking_work_type_mapping:
        this.time_tracking_work_type_mapping.$.map((mapping) => ({
          from_time_tracking_work_type_id:
            mapping.$.from_time_tracking_work_type_id.value,
          to_time_tracking_work_type_id:
            mapping.$.to_time_tracking_work_type_id.value!,
        })),
    };
  }
}
